import { useLocation } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { useCart } from "../context/CartContext";

/**
 * Floating cart button — top-right on every page except the system-admin area.
 * Opens the CartOverlay. Shows a live count badge.
 */
export default function GlobalCartButton() {
  const { count, open } = useCart();
  const location = useLocation();

  // Hide on system-admin routes and the tenant admin login/dashboard (not shopping surfaces).
  const path = location.pathname || "";
  if (path.startsWith("/admin")) return null;
  if (/\/[^/]+\/admin(\/|$)/.test(path)) return null;
  // Public expense-submission form is not a shopping surface — hide cart there.
  if (/\/[^/]+\/expense(\/|$)/.test(path)) return null;
  // Member Wallet / Dashboard has its own header with inline cart button.
  if (/\/[^/]+\/(dashboard|wallet|my-tickets)(\/|$)/.test(path)) return null;
  // Public shop pages use ShopHeader which now includes an inline cart button;
  // hide the floating one so the shopper sees ONE cart control on those pages.
  // Matches `/`, `/{slug}`, `/{slug}/event/*`, `/{slug}/checkout`, `/{slug}/login`.
  if (path === "/" || /^\/[^/]+\/?$/.test(path)) return null;
  if (/^\/[^/]+\/(event|checkout|login)(\/|$)/.test(path)) return null;

  return (
    <button
      onClick={open}
      className="fixed top-4 right-4 z-30 group flex items-center gap-2 pl-3 pr-4 py-2 rounded-full bg-white border border-slate-200 shadow-md hover:shadow-lg hover:border-[#1e3a5f] transition-all"
      data-testid="global-cart-btn"
      aria-label={`Open cart${count > 0 ? ` (${count} items)` : ""}`}
    >
      <span className="relative">
        <ShoppingCart className="w-5 h-5 text-[#1e3a5f]" />
        {count > 0 && (
          <span
            className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#2d8a4e] text-white text-[10px] font-extrabold flex items-center justify-center"
            data-testid="global-cart-count"
          >{count}</span>
        )}
      </span>
      <span className="text-sm font-semibold text-[#1e3a5f] hidden sm:inline">Cart</span>
    </button>
  );
}
